import React from "react";
import styles from "./index.module.scss";

/**
 * 保险所需资料页面
 * 由「事故维修 - 保险」下的「资料查看」进入。
 * 顶部标题与返回由原生导航栏提供（与预约页一致，通过 document.title 设置），页面内不自画导航。
 */

interface ChecklistItem {
  num: number;
  title: string;
  tag?: string;
  desc?: string;
  subItems?: string[];
}

// 基础清单
const CHECKLIST: ChecklistItem[] = [
  { num: 1, title: "行驶证", desc: "机动车行驶证正副本，需在有效期内" },
  { num: 2, title: "驾驶证", desc: "事故当事人驾驶证正副本，需在有效期内" },
  {
    num: 3,
    title: "被保人身份证",
    tag: "仅个人车辆",
    desc: "被保险人身份证正反面",
  },
  {
    num: 4,
    title: "事故证明",
    desc: "以下材料任选其一：",
    subItems: ["快处易赔事故认定书", "事故协议书"],
  },
  {
    num: 5,
    title: "保险公司报案号",
    desc: "致电保险公司报案后获取的报案编号",
  },
  {
    num: 6,
    title: "现场照片",
    desc: "建议拍摄以下角度：",
    subItems: ["车辆全景照", "碰撞部位近照", "对方车辆照片", "车辆位置关系照"],
  },
];

// 补充材料
const SUPPLEMENT: ChecklistItem[] = [
  { num: 7, title: "法人身份证", desc: "公司法定代表人身份证正反面" },
  { num: 8, title: "营业执照", desc: "原件照片/复印件盖公章" },
];

// 温馨提示
const TIPS: string[] = [
  "所有证件需在有效期内",
  "照片需清晰可辨，避免模糊反光",
  "事故现场请先拍照取证，再移动车辆。",
];

function renderItem(item: ChecklistItem) {
  return (
    <div className={styles.item} key={item.num}>
      <div className={styles.itemNum}>{item.num}</div>
      <div className={styles.itemBody}>
        <div className={styles.itemTitle}>
          {item.title}
          {item.tag && <span className={styles.itemTag}>{item.tag}</span>}
        </div>
        {item.desc && <div className={styles.itemDesc}>{item.desc}</div>}
        {item.subItems && (
          <ul className={styles.subList}>
            {item.subItems.map((sub, i) => (
              <li key={i}>{sub}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface InsuranceMaterialProps {
  onClose: () => void; // 返回预约项目
}

export default function InsuranceMaterial(props: InsuranceMaterialProps) {
  const { onClose } = props;

  return (
    <div className={styles.overlay}>
      <div className={styles.container}>
      {/* 顶部卡片 */}
      <div className={styles.headerCard}>
        <div className={styles.headerTitle}>
          <svg
            className={styles.headerIcon}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="5"
              y="4"
              width="14"
              height="17"
              rx="2"
              stroke="currentColor"
              strokeWidth="2"
            />
            <rect x="9" y="2.5" width="6" height="3" rx="1" fill="currentColor" />
            <path
              d="M8.5 11h7M8.5 15h5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          资料准备清单
        </div>
        <div className={styles.headerDesc}>
          请提前准备以下资料，材料齐全可加快理赔处理进度
        </div>
      </div>

      {/* 清单 */}
      <div className={styles.card}>{CHECKLIST.map(renderItem)}</div>

      {/* 补充材料分隔 */}
      <div className={styles.supplementLabel}>
        <span className={styles.supplementTag}>补充材料</span>
        <span className={styles.supplementNote}>
          公司车辆且定损金额超过1万元时需提供
        </span>
      </div>

      {/* 补充材料清单 */}
      <div className={`${styles.card} ${styles.supplementCard}`}>
        {SUPPLEMENT.map(renderItem)}
      </div>

      {/* 温馨提示 */}
      <div className={styles.tipsCard}>
        <div className={styles.tipsTitle}>
          <svg
            className={styles.tipsIcon}
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M12 3a6 6 0 0 0-3.5 10.9c.6.4 1 1.1 1 1.9v.2h5v-.2c0-.8.4-1.5 1-1.9A6 6 0 0 0 12 3z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <path
              d="M9.5 19h5M10 21h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          温馨提示
        </div>
        <div className={styles.tipsList}>
          {TIPS.map((tip, i) => (
            <div className={styles.tipItem} key={i}>
              <span className={styles.tipIndex}>{i + 1}.</span>
              <span className={styles.tipText}>{tip}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
      <div className={styles.bottomBar}>
        <div className={styles.returnBtn} onClick={onClose}>
          返回
        </div>
      </div>
    </div>
  );
}
